import { useState } from 'react';
import { api, ApiError } from '../lib/api';
import { useSession, useAsync } from '../lib/session';
import { inr } from '../lib/fmt';
import type { RoleKey } from '../lib/types';
import { Button, Card, Field, Input, Select, Modal, Note, PageHead, Pill, Tag,
         Table, Th, Td, TableWrap } from '../components/ui';
import { Loading, HeadSkeleton, TilesSkeleton, CardSkeleton, TableSkeleton } from '../components/skeleton';

interface UserRow {
  _id: string; id?: string; username: string; name: string; role: RoleKey;
  email?: string | null; phone?: string | null; approve_limit: number;
  is_active: boolean; must_change_pw: boolean; last_login_at?: string | null;
  language?: 'en' | 'hi';
}
interface RoleDef { key: RoleKey; label: string; who: string; cap_count: number; default_limit: number | null; caps: string[] }
interface Feed { users: UserRow[]; roles: RoleDef[]; caps: Record<string, string> }

/** Head, the people who can sign in, then what each role may do. */
function UsersSkeleton() {
  return (
    <Loading label="Loading users">
      <HeadSkeleton actions={1} />
      <CardSkeleton flush head={false} className="mb-5"><TableSkeleton rows={5} cols={5} /></CardSkeleton>
      <CardSkeleton><TableSkeleton rows={5} cols={3} cards={false} /></CardSkeleton>
    </Loading>
  );
}

export default function Users() {
  const { user } = useSession();
  const feed = useAsync<Feed>(() => api.get('/api/users'), []);
  const [edit, setEdit] = useState<UserRow | 'new' | null>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  if (feed.loading) return <UsersSkeleton />;
  if (feed.error) return <Note title="That did not load" tone="critical">{feed.error}</Note>;
  const d = feed.data!;

  async function revoke(u: UserRow) {
    setErr(''); setMsg('');
    try {
      await api.post(`/api/users/${u._id}/revoke-sessions`, {});
      setMsg(`${u.name} signed out everywhere.`);
    } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); }
  }

  return (
    <div>
      <PageHead eyebrow="Access" title="Users and permissions"
        dek="Two rules are enforced by the system, not by convention: nobody approves a claim they submitted, and every approver has a rupee ceiling above which a claim escalates."
        actions={<Button variant="primary" onClick={() => setEdit('new')}>Add a user</Button>} />

      {msg && <Note title="Done" tone="good">{msg}</Note>}
      {err && <Note title="That did not work" tone="critical">{err}</Note>}

      <Card flush className="mb-5">
        <TableWrap>
          <Table cards>
            <thead><tr>
              <Th>Person</Th><Th>Username</Th><Th>Role</Th><Th n>Approval ceiling</Th>
              <Th>Last signed in</Th><Th>State</Th><Th />
            </tr></thead>
            <tbody>
              {d.users.map((u) => (
                <tr key={u._id} className={u.is_active ? '' : 'opacity-55'}>
                  <Td label="Person"><b>{u.name}</b>{u._id === user?.id && <> <Tag>you</Tag></>}</Td>
                  <Td label="Username" className="font-mono text-[11.5px]">{u.username}</Td>
                  <Td label="Role"><Tag>{d.roles.find((r) => r.key === u.role)?.label ?? u.role}</Tag></Td>
                  <Td label="Approval ceiling" n>{u.role === 'super_admin' ? 'No limit' : inr(u.approve_limit)}</Td>
                  <Td label="Last signed in" className="font-mono text-[11.5px] text-muted">
                    {u.last_login_at ? String(u.last_login_at).slice(0, 16).replace('T', ' ') : 'never'}
                  </Td>
                  <Td label="State">
                    <Pill kind={u.is_active ? 'active' : 'draft'}>{u.is_active ? 'active' : 'disabled'}</Pill>
                    {u.must_change_pw && <> <Tag>starter password</Tag></>}
                  </Td>
                  <Td label="" className="text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => setEdit(u)}>Edit</Button>{' '}
                    <Button size="sm" variant="ghost" onClick={() => void revoke(u)}>Sign out</Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableWrap>
      </Card>

      <Card title="What each role can do"
            sub="Capabilities are checked at the point of action on the server. Hiding a button is a courtesy; the refusal is real.">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3.5">
          {d.roles.map((r) => (
            <div key={r.key} className="border border-rule rounded p-4">
              <div className="flex items-baseline justify-between gap-2.5 mb-2">
                <b className="font-display text-sm font-semibold">{r.label}</b>
                <Tag>{r.default_limit === null ? 'no ceiling' : `up to ${inr(r.default_limit)}`}</Tag>
              </div>
              <p className="m-0 mb-2.5 text-[12.5px] text-ink-2 leading-relaxed">{r.who}</p>
              <details>
                <summary className="cursor-pointer text-[11.5px] text-muted">{r.cap_count} capabilities</summary>
                <div className="flex flex-wrap gap-1 mt-2.5">
                  {r.caps.map((c) => <Tag key={c}>{c}</Tag>)}
                </div>
              </details>
            </div>
          ))}
        </div>
      </Card>

      {edit && <UserModal u={edit === 'new' ? null : edit} roles={d.roles}
                          onClose={() => setEdit(null)} onDone={() => { setEdit(null); feed.reload(); }} />}
    </div>
  );
}

function UserModal(
  { u, roles, onClose, onDone }:
  { u: UserRow | null; roles: RoleDef[]; onClose: () => void; onDone: () => void },
) {
  const [f, setF] = useState({
    name: u?.name ?? '', username: u?.username ?? '',
    role: u?.role ?? ('executive' as RoleKey),
    email: u?.email ?? '', phone: u?.phone ?? '',
    approve_limit: u ? String(u.approve_limit / 100) : '',
    language: u?.language ?? 'en', password: '',
  });
  const [active, setActive] = useState(u?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const chosen = roles.find((r) => r.key === f.role);

  return (
    <Modal title={u ? `Edit ${u.name}` : 'Add a user'} wide onClose={onClose}
      confirmLabel={u ? 'Save' : 'Create user'} busy={busy}
      onConfirm={async () => {
        setBusy(true); setErr('');
        try {
          await api.post('/api/users', {
            id: u?._id, name: f.name.trim(), username: f.username.trim().toLowerCase(),
            role: f.role, email: f.email.trim() || null, phone: f.phone.trim() || null,
            approve_limit: f.approve_limit === '' ? 0 : Math.round(Number(f.approve_limit) * 100),
            language: f.language, is_active: active,
            password: f.password || undefined,
          });
          onDone();
        } catch (e) { setErr(e instanceof ApiError ? e.message : String(e)); setBusy(false); }
      }}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
        <Field label="Name"><Input value={f.name} onChange={set('name')} /></Field>
        <Field label="Username"><Input value={f.username} onChange={set('username')} disabled={Boolean(u)} /></Field>
        <Field label="Role">
          <Select value={f.role} onChange={(e) => {
            const role = e.target.value as RoleKey;
            const def = roles.find((r) => r.key === role);
            setF({ ...f, role, approve_limit: !u && def
              ? (def.default_limit === null ? '' : String(def.default_limit / 100)) : f.approve_limit });
          }}>
            {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </Select>
        </Field>
        <Field label="Approval ceiling (₹)">
          <Input type="number" step="1" value={f.approve_limit} onChange={set('approve_limit')} />
        </Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={set('email')} /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={set('phone')} /></Field>
        <Field label="Screen language">
          <Select value={f.language} onChange={set('language')}>
            <option value="en">English</option>
            <option value="hi">हिन्दी — Hindi</option>
          </Select>
        </Field>
        <Field label={u ? 'Reset password' : 'Starter password'}>
          <Input type="password" value={f.password} onChange={set('password')}
                 placeholder={u ? 'Leave blank to keep' : 'At least 10 characters'} />
        </Field>
        <label className="flex items-center gap-2 text-[13.5px] text-ink-2 sm:col-span-2">
          <input type="checkbox" className="w-4 h-4" checked={active}
                 onChange={(e) => setActive(e.target.checked)} />Active
        </label>
      </div>
      {chosen && <p className="mt-3.5 text-[12.5px] text-ink-2">{chosen.who}</p>}
      {err && <p className="mt-2 text-[12.5px] text-critical">{err}</p>}
    </Modal>
  );
}
